import { i18n } from 'i18n';
import { LocalizedCountry } from 'queries';
import { Row } from 'react-table';
import { ParticipantTag } from 'store';
import { EMPTY_VALUES_ONLY } from './ComlumnsFilters/MinMaxFilter';

function getRowValueByColumnID(row: Row, columnId: string): any {
  return row.values[columnId];
}

function checkFilterRow(
  row: Row,
  columnId: string,
  filterValue: any,
  filterCondition: boolean,
) {
  const rowValue = getRowValueByColumnID(row, columnId);
  if (filterValue === null) {
    return !rowValue;
  }
  return filterCondition;
}

export const caseInsensitiveSort = (rowA: Row, rowB: Row, columnId: string) => {
  let valueA = getRowValueByColumnID(rowA, columnId);
  let valueB = getRowValueByColumnID(rowB, columnId);

  // Force to strings (or "" for unsupported types)
  const a = `${valueA || ''}`;
  const b = `${valueB || ''}`;

  return a.localeCompare(b, i18n.language);
};

export const numberSort = (rowA: Row, rowB: Row, columnId: string) => {
  const a = Number(getRowValueByColumnID(rowA, columnId));
  const b = Number(getRowValueByColumnID(rowB, columnId));

  if (isNaN(a) && isNaN(b)) {
    return 0;
  }

  if (isNaN(a) || isNaN(b)) {
    return isNaN(a) ? -1 : 1;
  }

  return a - b;
};

export const startsWithFilter = (
  rows: Row[],
  id: string,
  filterValue: string | null,
) => {
  const filteredRows = rows.filter((row: Row) => {
    return checkFilterRow(
      row,
      id,
      filterValue,
      getRowValueByColumnID(row, id) &&
        getRowValueByColumnID(row, id).startsWith(filterValue),
    );
  });
  if (filterValue === null && !filteredRows.length) {
    return rows;
  }

  return filteredRows;
};

startsWithFilter.autoRemove = (val?: string) => val === undefined;

export const tagsFilter = (
  rows: Row[],
  id: string,
  filterValue: { include: ParticipantTag[]; exclude: ParticipantTag[] } | null,
) => {
  const filteredRows = rows.filter((row: Row<any>) => {
    const tagsIds = row.original.tags;
    let selected = true;
    if (filterValue) {
      if (filterValue.include && filterValue.include.length) {
        selected =
          selected &&
          tagsIds &&
          !!filterValue.include.find((val) => tagsIds.includes(val.id));
      }

      if (filterValue.exclude && filterValue.exclude.length && tagsIds) {
        selected =
          selected &&
          !filterValue.exclude.find((val) => tagsIds.includes(val.id));
      }
    }

    return checkFilterRow(row, id, filterValue, selected);
  });

  if (filterValue === null && !filteredRows.length) {
    return rows;
  }

  return filteredRows;
};
tagsFilter.autoRemove = (val?: string[]) =>
  val === undefined || (val && val.length === 0);

export const arrayFilter = (
  rows: Row[],
  id: string,
  filterValue: string[] | null,
) => {
  const filteredRows = rows.filter((row: Row) => {
    const rowValue = getRowValueByColumnID(row, id);
    return checkFilterRow(
      row,
      id,
      filterValue,
      !!(
        rowValue &&
        filterValue &&
        filterValue.find((val) => val === rowValue)
      ),
    );
  });

  if (filterValue === null && !filteredRows.length) {
    return rows;
  }

  return filteredRows;
};
arrayFilter.autoRemove = (val?: string[]) =>
  val === undefined || (val && val.length === 0);

export const countrySort =
  (countries: { [id: string]: LocalizedCountry }) =>
  (rowA: Row, rowB: Row, columnId: string) => {
    const valueA = getRowValueByColumnID(rowA, columnId);
    const valueB = getRowValueByColumnID(rowB, columnId);

    const countryA = countries[valueA];
    const countryB = countries[valueB];

    const a = countryA?.text || '';
    const b = countryB?.text || '';

    return a.localeCompare(b, i18n.language);
  };

export const text = (rows: Row[], id: string, filterValue: string | null) => {
  const filteredRows = rows.filter((row) => {
    const rowValue = getRowValueByColumnID(row, id);
    return checkFilterRow(
      row,
      id,
      filterValue,
      String(rowValue)
        .toLowerCase()
        .includes(String(filterValue).toLowerCase()),
    );
  });

  if (filterValue === null && !filteredRows.length) {
    return rows;
  }

  return filteredRows;
};

text.autoRemove = (val?: string) => val === undefined;

export const between = (
  rows: Row[],
  id: string,
  filterValue: number[] | [string],
) => {
  if (filterValue[0] === EMPTY_VALUES_ONLY) {
    const filteredRows = rows.filter((row) => {
      const rowValue = getRowValueByColumnID(row, id);
      return !rowValue;
    });

    if (!filteredRows.length) {
      return rows;
    }

    return filteredRows;
  }
  let [min, max] = filterValue || [];
  min = typeof min === 'number' ? min : -Infinity;
  max = typeof max === 'number' ? max : Infinity;

  if (min > max) {
    const temp = min;
    min = max;
    max = temp;
  }

  return rows.filter((row) => {
    const rowValue = getRowValueByColumnID(row, id);
    if (filterValue === null) {
      return !rowValue;
    }
    return rowValue >= min && rowValue <= max;
  });
};

between.autoRemove = (val?: number[] | [string]) =>
  !val ||
  (typeof val[0] !== 'number' &&
    val[0] !== EMPTY_VALUES_ONLY &&
    typeof val[1] !== 'number');
